import { useContext, useEffect, useMemo, useState } from "react";
import { Accordion, Button, Col, Row } from "react-bootstrap";
import PractitionerInfo from "./practitioner-info";
import usePractitioner from "lib/hooks/usePractitioner";
import SimpleDataTable from "lib/components/common/simple-data-table";
import { PRACTITIONERS_COLUMNS } from "lib/constants/table-columns";
import ErrorBox from "lib/components/common/error-box";
import DashboardWidget from "lib/components/common/dashboard/dashboard-widget";
import { PiEnvelopeSimpleThin, PiUsersLight } from "react-icons/pi";
import LoadingBox from "lib/components/common/loading-box";
import { AppContext } from "lib/contexts/app-context";
import FlexBox from "lib/components/common/flex-box";
import InvitePractitionerModal from "./invite-practitioner-modal";
import { PRACTITIONER_STATUS } from "lib/constants/enums";
import { toast } from "sonner";

const Practitioners = () => {
  const { appState } = useContext<AppState>(AppContext);
  const [currentPractitioner, setCurrentPractitioner] =
    useState<PractitionerDetails | null>(null);
  //const [showModal, setShowModal] = useState<boolean>(false);
  const [showInviteModal, setShowInviteModal] = useState<boolean>(false);

  const {
    practitioners,
    getAllPractitioners,
    loading,
    error,
    rejectPractitioner,
    approvePractitioner,
    disablePractitioner,
    deletePractitioner,
  } = usePractitioner();

  const {
    invitePractitioners,
    error: invitePractitionersError,
    loading: invitePractitionersLoading,
  } = usePractitioner();

  const onClose = () => {
    //setShowModal(false);
    setCurrentPractitioner(null);
  };

  const onCloseInviteModal = () => {
    setShowInviteModal(false);
  };

  const onApprove = () => {
    approvePractitioner(currentPractitioner.id, () => {
      getAllPractitioners();
    });
    onClose();
  };

  const onReject = () => {
    if (currentPractitioner) {
      rejectPractitioner(currentPractitioner.id, () => {
        getAllPractitioners();
      });
    }
    onClose();
  };
  const onDisable = (value: boolean) => {
    if (currentPractitioner) {
      disablePractitioner(
        currentPractitioner?.id,
        value,
        (updatedPractitioner) => {
          toast(
            `Practitioner ${
              updatedPractitioner?.isDisabled ? "disabled" : "enabled"
            } sucessfully`
          );
          getAllPractitioners();
        }
      );
    }
    onClose();
  };

  const onDelete = () => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this practitioner? All related data will be wiped from the database and cannot be recovered."
    );

    if (!confirmed) return;
    deletePractitioner(currentPractitioner.id, () => {
      getAllPractitioners();
    });
    onClose();
  };

  const onPractitionerSubscriptionCancel = (clinicIds: string[]) => {
    console.log(clinicIds);
  };

  const getPendingPractitioners = useMemo(
    () =>
      (practitioners ?? []).filter(
        (practitioner) =>
          practitioner.status === PRACTITIONER_STATUS.PENDING ||
          practitioner.status === ""
      ),
    [practitioners]
  );

  const getApprovedPractitioners = useMemo(
    () =>
      (practitioners ?? []).filter(
        (practitioner) => practitioner.status === PRACTITIONER_STATUS.APPROVED && !practitioner.isDisabled
      ),
    [practitioners]
  );

  const getRejectedPractitioners = useMemo(
    () =>
      (practitioners ?? []).filter(
        (practitioner) => practitioner.status === PRACTITIONER_STATUS.REJECTED || practitioner.isDisabled
      ) ?? [],
    [practitioners]
  );

  useEffect(() => {
    getAllPractitioners();
  }, []);

  // const handleAddPractitioner = (email: string) => {
  //   setEmails((prev) => [...prev, email]);
  // };

  // const onRemoveEmail = (email: string) => {
  //   const newArray = removeArrayItem<string>(emails, null, email);
  //   setEmails(newArray);
  // };

  const handleInvite = (data: { email: string; name: string }) => {
    invitePractitioners(data.email, data.name, () => {
      setShowInviteModal(false);
    });
  };

  return (
    <>
      <Row>
        <Col sm="8">
          {error ? (
            <ErrorBox message={error} />
          ) : (
            <>
              {loading ? (
                <LoadingBox message="Loading practitioners..." />
              ) : (
                <Accordion defaultActiveKey="0">
                  <Accordion.Item eventKey="0">
                    <Accordion.Header>
                      <strong>Practitioners Awaiting Approval</strong>
                    </Accordion.Header>
                    <Accordion.Body>
                      <SimpleDataTable
                        columns={PRACTITIONERS_COLUMNS}
                        data={getPendingPractitioners}
                        onRowClick={(value: PractitionerDetails) => {
                          setCurrentPractitioner(value);
                        }}
                        isShowPagination={false}
                      />
                    </Accordion.Body>
                  </Accordion.Item>
                  <Accordion.Item eventKey="1">
                    <Accordion.Header>
                      <strong>Approved Practitioners</strong>
                    </Accordion.Header>
                    <Accordion.Body>
                      <SimpleDataTable
                        columns={PRACTITIONERS_COLUMNS}
                        data={getApprovedPractitioners}
                        onRowClick={(value: PractitionerDetails) => {
                          setCurrentPractitioner(value);
                        }}
                        isShowPagination={false}
                      />
                    </Accordion.Body>
                  </Accordion.Item>
                  <Accordion.Item eventKey="2">
                    <Accordion.Header>
                      <strong>Rejected Practitioners</strong>
                    </Accordion.Header>
                    <Accordion.Body>
                      <SimpleDataTable
                        columns={PRACTITIONERS_COLUMNS}
                        data={getRejectedPractitioners}
                        onRowClick={(value: PractitionerDetails) => {
                          setCurrentPractitioner(value);
                        }}
                        isShowPagination={false}
                      />
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
              )}
            </>
          )}
        </Col>
        <Col sm="4">
          <Row className="g-3">
            <Col xs="12">
              <Button
                className="w-100 p-0 border-secondary"
                variant="outline-primary"
                onClick={() => setShowInviteModal(true)}
              >
                <FlexBox
                  justifyContent="center"
                  alignItems="center"
                  direction="column"
                  className="h-100"
                >
                  <PiEnvelopeSimpleThin size={124} />
                  <h5 className="text-gray-600 px-4">Invite Practitioners</h5>
                </FlexBox>
              </Button>
            </Col>
            <Col xs="12">
              <DashboardWidget
                className="mb-0"
                icon={<PiUsersLight size={32} />}
                title="Need Approval"
                count={getPendingPractitioners.length}
                total={practitioners.length}
                isDashboard={false}
                description={
                  getPendingPractitioners.length > 0
                    ? "Practitioners are awaiting approval."
                    : "All done!"
                }
              />
            </Col>
            <Col xs="12">
              <DashboardWidget
                className="mb-0"
                icon={<PiUsersLight size={32} />}
                title="Rejected"
                count={getRejectedPractitioners.length}
                total={practitioners.length}
                isDashboard={false}
                description={
                  getPendingPractitioners.length > 0
                    ? "Practitioners are rejected."
                    : "All done!"
                }
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <PractitionerInfo
        practitionerInfo={currentPractitioner}
        open={!!currentPractitioner}
        onClose={() => setCurrentPractitioner(null)}
        onReject={appState?.userRole !== "receptionist" ? onReject : null}
        onApprove={appState?.userRole !== "receptionist" ? onApprove : null}
        onDisable={appState?.userRole !== "receptionist" ? onDisable : null}
        onDelete={appState?.userRole !== "receptionist" ? onDelete : null}
      />
      <InvitePractitionerModal
        open={showInviteModal}
        onClose={onCloseInviteModal}
        onInvite={handleInvite}
        error={invitePractitionersError}
        loading={invitePractitionersLoading}
      />
    </>
  );
};

export default Practitioners;
